import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { Device, DeviceCreate, DeviceUpdate } from '../types/Device';
import { useLogging } from './LoggingContext';
import httpClient, { tokenStorage } from '../services/httpClient';

// Base API URL - Use relative URL for nginx proxy in both local and prod
const API_URL = '/api';
// Socket.IO URL - Prefer same origin by default, allow override via env at build
const SOCKET_URL = (process.env.REACT_APP_SOCKET_URL as string) || window.location.origin;

// Debounce time for API calls (in milliseconds)
const FETCH_DEBOUNCE_TIME = 1000;

// SINGLETON API CLIENT
// This exists outside of React's component lifecycle to prevent re-render/remount issues
class APIClient {
  private static instance: APIClient;
  private socket: Socket | null = null;
  private deviceUpdateCallbacks: Array<(devices: Device[]) => void> = [];
  private deviceStatusCallbacks: Array<(device: Device) => void> = [];
  private lastDevices: Device[] = [];
  private isLoading = false;
  private lastFetchTime = 0;
  private fetchTimeout: NodeJS.Timeout | null = null;

  private constructor() {
    // Socket is lazy-connected once we have a token (see ensureSocket).
    if (tokenStorage.getAccess()) {
      this.connectSocket();
    }

    // Reconnetti con nuovo token quando cambia (login, refresh).
    window.addEventListener('auth:token-changed', () => this.reconnectSocket());
    window.addEventListener('auth:unauthorized', () => this.disconnectSocket());
  }

  public static getInstance(): APIClient {
    if (!APIClient.instance) {
      APIClient.instance = new APIClient();
    }
    return APIClient.instance;
  }

  public ensureSocket() {
    if (!this.socket && tokenStorage.getAccess()) {
      this.connectSocket();
    }
  }

  private disconnectSocket() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private reconnectSocket() {
    this.disconnectSocket();
    if (tokenStorage.getAccess()) this.connectSocket();
  }

  private connectSocket() {
    this.socket = io(SOCKET_URL, {
      transports: ['websocket'],
      path: '/socket.io',
      // Funzione: viene valutata a ogni (re)connect → token sempre fresco.
      auth: (cb) => cb({ token: tokenStorage.getAccess() || '' }),
    });

    this.socket.on('device_status', (data: Device) => {
      console.log(`SINGLETON: Device status update received: ${data.mqtt_id} is ${data.status}`);
      
      // First update our local cache of devices
      this.lastDevices = this.lastDevices.map(device => 
        device.id === data.id ? { ...device, ...data } : device
      );
      
      // If this device isn't in our lastDevices yet, add it
      if (!this.lastDevices.some(device => device.id === data.id)) {
        this.lastDevices.push(data);
      }
      
      // Notify all subscribers about the device status
      this.deviceStatusCallbacks.forEach(callback => callback(data));
    });

    // Fetch devices immediately on socket connection
    this.socket.on('connect', () => {
      console.log('SINGLETON: Socket connected, fetching devices');
      this.fetchDevices();
    });
  }

  public async fetchDevices(): Promise<Device[]> {
    // Prevent concurrent requests using debouncing pattern
    const now = Date.now();
    
    // If we're already loading or if we fetched very recently, return cached data
    if (this.isLoading || (now - this.lastFetchTime < FETCH_DEBOUNCE_TIME)) {
      console.log("SINGLETON: Skip fetch - already loading or recent fetch");
      return this.lastDevices;
    }

    // Clear any pending fetch timeout
    if (this.fetchTimeout) {
      clearTimeout(this.fetchTimeout);
      this.fetchTimeout = null;
    }

    // Set up a debounced fetch
    return new Promise<Device[]>((resolve) => {
      this.fetchTimeout = setTimeout(async () => {
        try {
          // Mark as loading to prevent concurrent requests
          this.isLoading = true;
          this.lastFetchTime = Date.now();
          
          console.log("SINGLETON: Fetching devices from API");
          
          // Try to fetch with exponential backoff retry
          let retries = 0;
          const maxRetries = 3;
          let response;
          
          while (retries <= maxRetries) {
            try {
              response = await httpClient.get<{ devices: Device[] }>(`/devices/`);
              break; // Success, exit the retry loop
            } catch (err) {
              if (retries === maxRetries) {
                throw err; // Re-throw if we've exhausted retries
              }
              
              // Calculate exponential backoff delay
              const delay = Math.min(1000 * Math.pow(2, retries), 5000);
              console.log(`SINGLETON: API request failed, retrying in ${delay}ms (attempt ${retries + 1}/${maxRetries + 1})`);
              
              // Wait before retry
              await new Promise(r => setTimeout(r, delay));
              retries++;
            }
          }
          
          if (response) {
            this.lastDevices = response.data.devices;
            
            // Notify all subscribers
            this.deviceUpdateCallbacks.forEach(callback => callback(this.lastDevices));
          }
          
          resolve(this.lastDevices);
        } catch (err) {
          console.error("SINGLETON: Error fetching devices after retries:", err);
          resolve(this.lastDevices);
        } finally {
          this.isLoading = false;
          this.fetchTimeout = null;
        }
      }, 50); // Small delay to allow for batching multiple calls
    });
  }

  public subscribeToDeviceUpdates(callback: (devices: Device[]) => void): () => void {
    this.deviceUpdateCallbacks.push(callback);
    
    // If we already have devices, call the callback immediately
    if (this.lastDevices.length > 0) {
      callback(this.lastDevices);
    } else {
      // Otherwise fetch devices if we don't have any yet
      this.fetchDevices();
    }
    
    // Return unsubscribe function
    return () => {
      this.deviceUpdateCallbacks = this.deviceUpdateCallbacks.filter(cb => cb !== callback);
    };
  }

  public subscribeToDeviceStatus(callback: (device: Device) => void): () => void {
    this.deviceStatusCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.deviceStatusCallbacks = this.deviceStatusCallbacks.filter(cb => cb !== callback);
    };
  }

  public async approveDevice(id: number): Promise<void> {
    try {
      await httpClient.post(`/devices/${id}/approve_connection`);
      console.log(`SINGLETON: Sent approval to device ${id}`);
    } catch (err) {
      console.error(`SINGLETON: Failed to send approval to device ${id}`, err);
      throw err;
    }
  }

  public async startSteering(id: number): Promise<void> {
    try {
      await httpClient.post(`/devices/${id}/start`);
      console.log(`SINGLETON: Sent start command to device ${id}`);
    } catch (err) {
      console.error(`SINGLETON: Failed to start steering on device ${id}`, err);
      throw err;
    }
  }

  public async stopSteering(id: number): Promise<void> {
    try {
      await httpClient.post(`/devices/${id}/stop`);
      console.log(`SINGLETON: Sent stop command to device ${id}`);
    } catch (err) {
      console.error(`SINGLETON: Failed to stop steering on device ${id}`, err);
      throw err;
    }
  }

  public async updateDevice(id: number, data: DeviceUpdate): Promise<void> {
    try {
      const response = await httpClient.put(`/devices/${id}`, data);
      
      // Update our local cache
      this.lastDevices = this.lastDevices.map(device => 
        device.id === id ? { ...device, ...response.data } : device
      );
      
      // Notify subscribers
      this.deviceUpdateCallbacks.forEach(callback => callback(this.lastDevices));
    } catch (err) {
      console.error(`SINGLETON: Failed to update device ${id}`, err);
      throw err;
    }
  }

  public async deleteDevice(id: number): Promise<void> {
    try {
      await httpClient.delete(`/devices/${id}`);
      
      // Update our local cache
      this.lastDevices = this.lastDevices.filter(device => device.id !== id);
      
      // Notify subscribers
      this.deviceUpdateCallbacks.forEach(callback => callback(this.lastDevices));
    } catch (err) {
      console.error(`SINGLETON: Failed to delete device ${id}`, err);
      throw err;
    }
  }

  public async switchController(id: number, controllerType: string, pValue: number, iValue?: number): Promise<void> {
    try {
      await httpClient.post(`/devices/${id}/switch_controller`, { 
        type: controllerType, 
        p_value: pValue, 
        i_value: iValue 
      });
      console.log(`SINGLETON: Switched controller to ${controllerType} for device ${id}`);
    } catch (err) {
      console.error(`SINGLETON: Failed to switch controller on device ${id}`, err);
      throw err;
    }
  }

  public dispose() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.deviceUpdateCallbacks = [];
    this.deviceStatusCallbacks = [];
  }
}

// Create singleton instance outside of React's component lifecycle
const apiClient = APIClient.getInstance();

interface DevicesContextType {
    devices: Device[];
    loading: boolean;
    error: string | null;
    connectionStatus: 'connected' | 'connecting' | 'error';
    fetchDevices: () => Promise<void>;
    updateDevice: (id: number, device: DeviceUpdate) => Promise<void>;
    deleteDevice: (id: number) => Promise<void>;
    approveDevice: (id: number) => Promise<void>;
    disconnectDevice: (id: number) => Promise<void>;
    startSteering: (id: number) => Promise<void>;
    stopSteering: (id: number) => Promise<void>;
    switchController: (id: number, controllerType: string, pValue: number, iValue?: number) => Promise<void>;
}

const DevicesContext = createContext<DevicesContextType | undefined>(undefined);

// Wrapper component to access LoggingContext
// (necessary because we can't use hooks directly in the provider)
export const DevicesProviderWithLogging: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const logging = useLogging();
    return <DevicesProviderBase loggingService={logging}>{children}</DevicesProviderBase>;
};

interface DevicesProviderBaseProps {
    children: React.ReactNode;
    loggingService: {
        addLog: (log: any) => void;
    };
}

// Main provider that accepts logging service as a prop
export const DevicesProviderBase: React.FC<DevicesProviderBaseProps> = ({ 
    children, 
    loggingService 
}) => {
    const [devices, setDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'error'>('connecting');

    const getDeviceNameOrMqttId = useCallback((id: number): string => {
        const device = devices.find(d => d.id === id);
        return device ? (device.name || device.mqtt_id) : `Device ${id}`;
    }, [devices]);

    // Use the singleton apiClient
    useEffect(() => {
        setLoading(true);
        setConnectionStatus('connecting');
        console.log("DevicesContext: Setting up API client subscriptions");
        
        // Subscribe to device updates
        const deviceUpdateUnsubscribe = apiClient.subscribeToDeviceUpdates((updatedDevices) => {
            console.log(`Received ${updatedDevices.length} devices from API client`);
            setDevices(updatedDevices);
            setLoading(false);
            setError(null);
            setConnectionStatus('connected');
        });
        
        // Subscribe to device status updates
        const deviceStatusUnsubscribe = apiClient.subscribeToDeviceStatus((updatedDevice) => {
            const deviceName = updatedDevice.name || updatedDevice.mqtt_id || `Device ${updatedDevice.id}`;
            
            // Update the devices array in React state
            setDevices(prevDevices => 
                prevDevices.map(device => 
                    device.id === updatedDevice.id 
                        ? { ...device, ...updatedDevice }
                        : device
                )
            );
            
            loggingService.addLog({
                message: `Device status update: ${deviceName} is now ${updatedDevice.status}`,
                deviceId: updatedDevice.id,
                deviceName,
                level: 'info',
                source: 'system',
                data: updatedDevice
            });
        });

        // Clean up subscriptions on unmount
        return () => {
            deviceUpdateUnsubscribe();
            deviceStatusUnsubscribe();
        };
    }, []);

    // Wrapped API methods that handle UI state and logging
    const fetchDevices = useCallback(async () => {
        try {
            setLoading(true);
            setConnectionStatus('connecting');
            await apiClient.fetchDevices();
            setConnectionStatus('connected');
            loggingService.addLog({
                message: `Manually refreshed device list`,
                level: 'info',
                source: 'user',
            });
        } catch (err) {
            setError('Failed to fetch devices');
            setConnectionStatus('error');
            loggingService.addLog({
                message: 'Failed to fetch devices from server',
                level: 'error',
                source: 'system',
            });
        } finally {
            setLoading(false);
        }
    }, [loggingService.addLog]);

    const updateDevice = useCallback(async (id: number, deviceData: DeviceUpdate) => {
        const deviceName = getDeviceNameOrMqttId(id);
        try {
            await apiClient.updateDevice(id, deviceData);
            loggingService.addLog({
                message: `Updated device settings for ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'info',
                source: 'user',
            });
        } catch (err) {
            setError(`Failed to update ${deviceName}`);
            loggingService.addLog({
                message: `Failed to update device settings for ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'error',
                source: 'user',
            });
            throw err;
        }
    }, [loggingService.addLog, getDeviceNameOrMqttId]);

    const deleteDevice = useCallback(async (id: number) => {
        const deviceName = getDeviceNameOrMqttId(id);
        try {
            await apiClient.deleteDevice(id);
            loggingService.addLog({
                message: `Deleted device ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'warning',
                source: 'user',
            });
        } catch (err) {
            setError(`Failed to delete ${deviceName}`);
            loggingService.addLog({
                message: `Failed to delete device ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'error',
                source: 'user',
            });
            throw err;
        }
    }, [loggingService.addLog, getDeviceNameOrMqttId]);

    const approveDevice = useCallback(async (id: number) => {
        const deviceName = getDeviceNameOrMqttId(id);
        try {
            await apiClient.approveDevice(id);
            loggingService.addLog({
                message: `Sent approval to device ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'info',
                source: 'user',
            });
            // Status update will come via WebSocket
        } catch (err) {
            setError(`Failed to send approval to ${deviceName}`);
            loggingService.addLog({
                message: `Failed to send approval to ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'error',
                source: 'user',
            });
            throw err;
        }
    }, [loggingService.addLog, getDeviceNameOrMqttId]);

    const disconnectDevice = useCallback(async (deviceId: number) => {
        const deviceName = getDeviceNameOrMqttId(deviceId);
        try {
            await httpClient.post(`/devices/${deviceId}/disconnect`);
            loggingService.addLog({
                message: `Sent disconnect request for device ${deviceName} (SSH logic)`, 
                deviceId,
                deviceName,
                level: 'info',
                source: 'user' 
            });
        } catch (error) {
            setError(`Failed to disconnect device ${deviceName}`);
                loggingService.addLog({
                message: `Failed to disconnect device ${deviceName} (SSH logic)`, 
                    deviceId,
                    deviceName,
                    level: 'error',
                source: 'user' 
                });
            throw error;
        }
    }, [loggingService.addLog, getDeviceNameOrMqttId]);

    const startSteering = useCallback(async (id: number) => {
        const deviceName = getDeviceNameOrMqttId(id);
        try {
            await apiClient.startSteering(id);
            loggingService.addLog({
                message: `Sent start command to device ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'info',
                source: 'user',
            });
        } catch (err) {
            setError(`Failed to start steering on ${deviceName}`);
                loggingService.addLog({
                message: `Failed to start steering on ${deviceName}`,
                deviceId: id,
                    deviceName,
                    level: 'error',
                    source: 'user',
                });
            throw err;
            }
    }, [loggingService.addLog, getDeviceNameOrMqttId]);

    const stopSteering = useCallback(async (id: number) => {
        const deviceName = getDeviceNameOrMqttId(id);
        try {
            await apiClient.stopSteering(id);
            loggingService.addLog({
                message: `Sent stop command to device ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'info',
                source: 'user',
            });
        } catch (err) {
            setError(`Failed to stop steering on ${deviceName}`);
                loggingService.addLog({
                message: `Failed to stop steering on ${deviceName}`,
                deviceId: id,
                    deviceName,
                    level: 'error',
                    source: 'user',
                });
            throw err;
            }
    }, [loggingService.addLog, getDeviceNameOrMqttId]);

    const switchController = useCallback(async (id: number, controllerType: string, pValue: number, iValue?: number) => {
        const deviceName = getDeviceNameOrMqttId(id);
        try {
            await apiClient.switchController(id, controllerType, pValue, iValue);
            loggingService.addLog({
                message: `Switched controller to ${controllerType} for device ${deviceName}`,
                deviceId: id,
                deviceName,
                controllerType,
                level: 'info',
                source: 'user',
            });
        } catch (err) {
            setError(`Failed to switch controller on ${deviceName}`);
            loggingService.addLog({
                message: `Failed to switch controller on ${deviceName}`,
                deviceId: id,
                deviceName,
                level: 'error',
                source: 'user',
            });
            throw err;
        }
    }, [loggingService.addLog, getDeviceNameOrMqttId]);

    // Create context value
    const contextValue = {
                devices,
                loading,
                error,
        connectionStatus,
                fetchDevices,
                updateDevice,
                deleteDevice,
        approveDevice, 
                disconnectDevice,
                startSteering,
                stopSteering,
                switchController,
    };

    return (
        <DevicesContext.Provider value={contextValue}>
            {children}
        </DevicesContext.Provider>
    );
};

// For backward compatibility, export a simple provider
// (this will only be used if we decide not to use the LoggingProvider)
export const DevicesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // A dummy logging service that does nothing
    const noopLoggingService = {
        addLog: () => {}
    };
    
    return <DevicesProviderBase loggingService={noopLoggingService}>{children}</DevicesProviderBase>;
};

export const useDevices = () => {
    const context = useContext(DevicesContext);
    if (context === undefined) {
        throw new Error('useDevices must be used within a DevicesProvider');
    }
    return context;
}; 